import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery, useMutation, gql } from '@zenbusiness/application-commons-graphql';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import { Navigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import ArtBubble from '#client/components/ArtBubble';
import { getErrorsByField, throwExceptions } from '#client/utils/validation';

import { READ_DOMAINS } from '../DomainList';

export const CONFIGURE_EXTERNAL_DOMAIN_TO_WEBSITE = gql`
  mutation ConfigureExternalDomainToWebsiteInput($input: ConfigureExternalDomainToWebsiteInput!) {
    configureExternalDomainToWebsite(input: $input) {
      domainUuid
      domainName
      active
      configured
      domainPrivacy
      transferred
      expired
      verified
      srsExpireDatetime
      isExternal
      deleted
    }
  }
`;

export const GET_WEBSITE_UUIDS = gql`
  query WebsitesUUIDs($businessEntityUuid: UUID!) {
    websites(businessEntityUuid: $businessEntityUuid) {
      websites {
        websiteUuid
      }
    }
  }
`;

const StyledGridRoot = styled(Grid)(({ theme }) => ({
  flexDirection: 'column',
  justifyContent: 'flex-start',
  textAlign: 'center',
  width: 'calc(100vw - 40px)',
  '& *': {
    maxWidth: '100%'
  },
  [theme.breakpoints.up('sm')]: {
    position: 'relative',
    width: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& div': {
      maxWidth: '540px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }
  }
}));

const StyledGridTextBoxContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.up('md')]: {
    width: '358.55px'
  },
  width: '340px',
  justifyContent: 'center',
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
  marginLeft: 'auto',
  marginRight: 'auto'
}));

const StyledBlockItem = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5)
}));

const StyledButtonGroup = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: 0,
    width: '100vw',
    left: 0,
    margin: 0,
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'space-between',
    '& a,button': {
      borderRadius: 0,
      width: '100vw',
      height: '64px',
      opacity: 1,
      margin: 0,
      border: 'none'
    }
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '360px',
  textAlign: 'center',
  margin: theme.spacing(2.5),
  '&:disabled': {
    backgroundColor: theme.palette.peace10,
    color: theme.palette.focus10
  }
}));

const messages = {
  validation: {
    generic: 'Error',
    domainName: {
      format: 'Domain name is not formatted correctly.',
      domain_exists: 'Domain name is taken.',
      domain_exists_deleted: 'Domain name is taken. Please contact Customer Support.'
    }
  }
};

const ExternalDomainCreate = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const [domainName, updateDomainName] = useState('');

  const {
    loading: readWebsitesLoading,
    data: {
      websites: {
        websites = []
      } = {}
    } = {}
  } = useQuery(GET_WEBSITE_UUIDS, {
    variables: {
      businessEntityUuid
    }
  });

  const [configureExternalDomainToWebsite, {
    loading: configureDomainLoading,
    data: {
      configureExternalDomainToWebsite: domain = null
    } = {},
    error: configureDomainError
  }] = useMutation(CONFIGURE_EXTERNAL_DOMAIN_TO_WEBSITE, {
    noThrow: true,
    update: (cache, {
      data: {
        configureExternalDomainToWebsite: createdDomain
      }
    }) => {
      try {
        const { domains } = cache.readQuery({
          query: READ_DOMAINS,
          variables: {
            businessEntityUuid
          }
        })?.data || {};

        cache.writeQuery({
          query: READ_DOMAINS,
          variables: {
            businessEntityUuid
          },
          data: {
            domains: {
              ...domains,
              domains: [...domains.domains, createdDomain]
            }
          }
        });
      } catch {
        // Failed to update cache. The queried data doesn't exit in the cache,
        // because the user hit the page directly or refreshed the page. Ignore error
      }
    }
  });

  const handleExternalDomainNext = useButtonPressedCallback({
    elementName: 'next',
    elementText: 'Next',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'external_domains'
  });

  // Throw non-user-validation errors to error boundry
  throwExceptions(configureDomainError);

  // TODO Change with multiple website expansion
  const { websiteUuid } = websites[0] || {};
  const disabled = readWebsitesLoading || configureDomainLoading || !domainName;

  // You landed here without a website. Unexpected.
  if ((!readWebsitesLoading && !websiteUuid)) {
    return (<Navigate to={`/d/company/${businessEntityUuid}`} />);
  }

  // You configured your external domain to point at the website, now to enter dns records
  if (!configureDomainLoading && domain) {
    return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domain.domainUuid}/external-records`} />);
  }

  const createDomain = () => {
    configureExternalDomainToWebsite({
      variables: {
        input: {
          businessEntityUuid,
          domainName,
          websiteUuid
        }
      }
    });
  };

  const handleNextButtonClick = () => {
    createDomain();
    handleExternalDomainNext();
  };

  const handleEnterPressed = (event) => {
    if (event.charCode === 13) {
      handleNextButtonClick();
    }
  };

  const handleDomainNameChanged = (event) => {
    const {
      target: {
        value: updatedDomainName
      }
    } = event;
    updateDomainName(updatedDomainName);
  };

  // Process server validation errors
  const errorsByField = getErrorsByField(configureDomainError);
  let errorLabel = null;
  const errorKey = errorsByField.domainName && errorsByField.domainName[0];
  const errorMessage = errorsByField.domainName && errorsByField.domainName[1];
  if (messages.validation.domainName && errorKey) {
    errorLabel = messages.validation.domainName[errorKey]
    || errorMessage
    || messages.validation.generic;
  }
  const hasError = !!errorLabel;

  return (
    <StyledGridRoot container item onKeyPress={handleEnterPressed}>
      <div>
        <SharedAsset src="/illustration/greeter.svg" width={124} alt="Rovie" />
      </div>
      <StyledBlockItem>
        <Title level={1} variant="hg">
          What is your domain name?
        </Title>
        <Text size="sm">
          We will guide you through connecting your website to your domain registered elsewhere.
        </Text>
      </StyledBlockItem>
      <StyledGridTextBoxContainer container>
        <TextField
          id="domainName"
          variant="outlined"
          size="small"
          placeholder="Domain Name"
          onChange={handleDomainNameChanged}
          fullWidth
          error={hasError}
        />
      </StyledGridTextBoxContainer>

      {hasError && (
        <ArtBubble text={errorLabel} />
      )}

      <StyledButtonGroup>
        <StyledButton
          size="large"
          disabled={disabled}
          onClick={handleNextButtonClick}
          variant="contained"
          color="primary"
        >
          Next
        </StyledButton>
      </StyledButtonGroup>
    </StyledGridRoot>
  );
};

export default ExternalDomainCreate;
